/**
 * Data table + filter subsystem: jurisdiction/legislation/status filtering,
 * the combobox typeahead, table rendering, and column sorting.
 *
 * Extracted verbatim from the globe client monolith (Phase 2, PR-B). It talks
 * to the globe through the window.globe seam, opens region detail via the
 * window.openDrawer global, and reads the shared filter state on
 * window.__SELECTED_STATUSES__ / window.__SELECTED_TAGS__ / window.__*_TABLE_DATA__. Bootstrap-derived
 * combobox data and the view-toggle collapse helper are injected. Typed
 * against the window.globe (GlobeApi) seam and the bootstrap table-data rows.
 */
/* eslint-disable */
import {
  formatLegislationTags,
  matchesSelectedLegislationTags,
  mergeLegislationTagCounts,
} from '../../../data/age-assurance/tags'
import { STATUS_CLASSES, STATUS_LABELS } from '../../../views/stickers'
import { getDataType } from '../data-type'

export interface TableFilterDeps {
  regionData: any[]
  subnationalSearchData: any[]
  collapseFilterContentIfExpanded: () => void
}

export interface TableFilterApi {
  renderTableBody: (dataType: string) => void
  applyStatusFilter: () => void
  applyDigitalIdFilters: () => void
  resetDigitalIdFilters: () => void
  resetStatusFilter: () => void
}

export function initTableFilter(deps: TableFilterDeps): TableFilterApi {
    var regionData = deps.regionData;
    var subnationalSearchData = deps.subnationalSearchData;
    var collapseFilterContentIfExpanded = deps.collapseFilterContentIfExpanded;

    // Current table sort state (shared by header rendering + the sort handler).
    var sortColumn = 'jurisdiction';
    var sortDir = 'asc';

    function getFilteredRegionData() {
      var sel = document.getElementById('jurisdiction-level-select') as HTMLInputElement | null;
      var level = sel ? sel.value : '';
      if (level === 'national') return regionData.filter(function(r) { return r.jurisdictionLevel === 'national' && r.code !== 'EU'; });
      if (level === 'subnational') return subnationalSearchData;
      if (level === 'eu') return regionData.filter(function(r) { return r.code === 'EU'; });
      // No filter: return country entries plus individual subnational entries, deduplicated by code
      var regionCodes = new Set(regionData.map(function(r) { return r.code; }));
      var uniqueSubnational = subnationalSearchData.filter(function(r) { return !regionCodes.has(r.code); });
      return regionData.concat(uniqueSubnational).sort(function(a, b) { return a.name.localeCompare(b.name); });
    }

    function getComboboxData() {
      var dataType = getDataType();
      var level = (document.getElementById('jurisdiction-level-select') as HTMLInputElement | null)?.value || '';
      if (dataType === 'digital_ids') {
        var digitalData = window.__DIGITAL_IDS_TABLE_DATA__ || [];
        if (level === 'subnational') return [];
        return digitalData.map(function(r) {
          return { code: r.code, name: r.name, searchName: r.searchName, href: r.href || '/region/' + r.code.toLowerCase(), jurisdictionLevel: 'national', count: 1 };
        });
      }
      return getFilteredRegionData();
    }

    function applyJurisdictionLevelFilter() {
      var level = (document.getElementById('jurisdiction-level-select') as HTMLInputElement | null)?.value || '';
      var tbody = document.getElementById('table-body');
      if (!tbody) return;
      var lastParentShow = true;
      (Array.from(tbody.children) as HTMLElement[]).forEach(function(row) {
        if (row.id === 'table-empty-state') return;
        if (row.classList.contains('table-row-child')) {
          row.style.display = lastParentShow ? '' : 'none';
        } else {
          lastParentShow = !level || row.getAttribute('data-jurisdiction-level') === level;
          row.style.display = lastParentShow ? '' : 'none';
        }
      });
      updateTableEmptyState();
    }

    function statusLabel(s) { return STATUS_LABELS[s] || s || 'Active'; }
    function statusSticker(s) { return '<span class="sticker ' + (STATUS_CLASSES[s] || 'sticker-dark-grey') + '">' + escapeHtml(statusLabel(s)) + '</span>'; }

    var CHEVRON_RIGHT_SVG = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>';

    // Column sets differ between data types: the Digital ID table drops the
    // age-assurance-specific "Tags" column and adds "Level of assurance" and
    // "Claims returned". The thead row is rewritten on every render so the
    // header always matches the body for the active data type.
    var AGE_ASSURANCE_COLUMNS = [['jurisdiction', 'Jurisdiction'], ['legislation', 'Legislation'], ['status', 'Statuses'], ['tags', 'Tags']];
    var DIGITAL_ID_COLUMNS = [['jurisdiction', 'Jurisdiction'], ['digitalIds', 'Digital IDs'], ['loa', 'Level of assurance'], ['claims', 'Claims returned']];

    function getDataTable() { return document.querySelector('.data-table'); }

    function renderTableHeader(dataType) {
      var table = getDataTable();
      var headRow = table && table.querySelector('thead tr');
      if (!headRow) return;
      var cols = dataType === 'digital_ids' ? DIGITAL_ID_COLUMNS : AGE_ASSURANCE_COLUMNS;
      // Default sort resets to jurisdiction ascending whenever columns change.
      sortColumn = 'jurisdiction';
      sortDir = 'asc';
      headRow.innerHTML = cols.map(function(c) {
        var isSortCol = c[0] === sortColumn;
        var sortClass = isSortCol ? ' sort-' + sortDir : '';
        var ariaSort = isSortCol ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
        return '<th class="sortable' + sortClass + '" data-sort="' + c[0] + '" aria-sort="' + ariaSort + '">' + escapeHtml(c[1]) + '<span class="sort-indicator" aria-hidden="true"></span></th>';
      }).join('');
    }

    function renderTableBody(dataType) {
      var tbody = document.getElementById('table-body');
      if (!tbody) return;
      renderTableHeader(dataType);
      var ageData = window.__AGE_ASSURANCE_TABLE_DATA__ || [];
      var digitalData = window.__DIGITAL_IDS_TABLE_DATA__ || [];
      var html = '';
      if (dataType === 'digital_ids') {
        digitalData.forEach(function(r) {
          var count = r.digitalIdCount || 0;
          var summary = count === 1 ? '1 digital ID' : count + ' digital IDs';
          var loa = r.levelsOfAssurance || [];
          var loaHtml = loa.length
            ? '<div class="cell-status-pills">' + loa.map(function(v) { return '<span class="sticker sticker-grey">' + escapeHtml(v) + '</span>'; }).join('') + '</div>'
            : '—';
          var claims = r.claims || [];
          var claimsText = claims.length ? escapeHtml(claims.join(', ')) : '—';
          var eudiStages = r.eudiReadinessStages || [];
          html += '<tr class="table-row" data-href="' + (r.href || '/region/' + r.code.toLowerCase()) + '" data-jurisdiction-level="national" data-loa="' + escapeHtml(loa.join('|')) + '" data-claims="' + escapeHtml(claims.join('|')) + '" data-eudi="' + escapeHtml(eudiStages.join('|')) + '">' +
            '<td class="cell-jurisdiction">' + escapeHtml(r.name) + '</td>' +
            '<td class="cell-legislation">' + escapeHtml(summary) + '</td>' +
            '<td class="cell-loa">' + loaHtml + '</td>' +
            '<td class="cell-claims">' + claimsText + '</td></tr>';
        });
      } else {
        ageData.forEach(function(c) {
          var status = c.status || 'active';
          var tagsText = escapeHtml(formatLegislationTags(c.tags)) || '—';
          if (c.hasRegionalLaws) {
            var parentTagCounts = c.legislationTagCounts || {};
            var perRegion = c.perRegionLegislationTagCounts || {};
            var isEU = c.code === 'EU';
            var jurisdictionLevel = isEU ? 'eu' : 'subnational';
            var childHrefPrefix = isEU ? '/region/' : '/country/' + c.code.toLowerCase() + '/region/';
            var parentLegCount = c.legislationCount || 0;
            var legLabel = parentLegCount + (parentLegCount === 1 ? ' law' : ' laws');
            var childStatusesAttr = (c.regionStatuses || []).filter(function(s) { return s !== 'none'; }).join(',');
            var parentLegStatuses = (c.legislationStatuses && c.legislationStatuses.length > 0) ? c.legislationStatuses : [status];
            var parentStatusesHtml = '<div class="cell-status-pills">' +
              parentLegStatuses.filter(function(s) { return s && s !== 'none'; })
                .map(statusSticker).join('') +
              '</div>';
            var parentFilterCounts = mergeLegislationTagCounts(
              parentTagCounts,
              ...Object.keys(perRegion).map(function(k) { return perRegion[k]; })
            );
            var parentTagCountsJson = JSON.stringify(parentFilterCounts).replace(/"/g, '&quot;');
            html += '<tr class="table-row table-row-accordion expanded" data-country-code="' + escapeHtml(c.code) + '" data-jurisdiction-level="' + jurisdictionLevel + '" data-status="' + escapeHtml(status) + '" data-child-statuses="' + escapeHtml(childStatusesAttr) + '" data-child-href-prefix="' + childHrefPrefix + '" data-leg-tag-counts="' + parentTagCountsJson + '">' +
              '<td class="cell-jurisdiction"><span class="accordion-chevron">' + CHEVRON_RIGHT_SVG + '</span>' + escapeHtml(c.name) + '</td>' +
              '<td class="cell-legislation">' + legLabel + '</td>' +
              '<td class="cell-status">' + parentStatusesHtml + '</td>' +
              '<td class="cell-tags">' + tagsText + '</td></tr>';
            // Render child rows inline (always expanded)
            (c.regionsList || []).forEach(function(r) {
              if (r.status === 'none') return;
              var rStatus = r.status || 'active';
              var rLegStatuses = (r.legislationStatuses && r.legislationStatuses.length > 0) ? r.legislationStatuses : [rStatus];
              var rLawCount = r.lawCount || 1;
              var rLegLabel = rLawCount + (rLawCount === 1 ? ' law' : ' laws');
              var rTags = escapeHtml(formatLegislationTags(r.tags)) || '—';
              var rTagCountsJson = JSON.stringify(perRegion[r.code.toUpperCase()] || {}).replace(/"/g, '&quot;');
              var rStatusesHtml = '<div class="cell-status-pills">' +
                rLegStatuses.filter(function(s) { return s && s !== 'none'; })
                  .map(statusSticker).join('') +
                '</div>';
              html += '<tr class="table-row table-row-child" data-href="' + childHrefPrefix + r.code.toLowerCase() + '" data-accordion-parent="' + escapeHtml(c.code) + '" data-status="' + escapeHtml(rStatus) + '" data-leg-tag-counts="' + rTagCountsJson + '">' +
                '<td class="cell-jurisdiction">' + escapeHtml(r.name) + '</td>' +
                '<td class="cell-legislation">' + rLegLabel + '</td>' +
                '<td class="cell-status">' + rStatusesHtml + '</td>' +
                '<td class="cell-tags">' + rTags + '</td></tr>';
            });
          } else {
            var tagCounts = c.legislationTagCounts || {};
            var isEuMember = c.isEuMember || false;
            var euMemberClass = isEuMember ? ' eu-member-row' : '';
            var euMemberStyle = isEuMember ? ' style="display:none"' : '';
            var legCount = c.legislationCount || 1;
            var legLabel = legCount + ' ' + (legCount === 1 ? 'law' : 'laws');
            var tagCountsJson = JSON.stringify(tagCounts).replace(/"/g, '&quot;');
            html += '<tr class="table-row' + euMemberClass + '" data-href="' + (c.href || '/region/' + c.code.toLowerCase()) + '" data-jurisdiction-level="national" data-status="' + escapeHtml(status) + '" data-leg-tag-counts="' + tagCountsJson + '"' + euMemberStyle + '>' +
              '<td class="cell-jurisdiction">' + escapeHtml(c.name) + '</td>' +
              '<td class="cell-legislation">' + legLabel + '</td>' +
              '<td class="cell-status">' + statusSticker(status) + '</td>' +
              '<td class="cell-tags">' + tagsText + '</td></tr>';
          }
        });
      }
      var emptyColspan = 4;
      html += '<tr id="table-empty-state" class="table-empty-state" style="display:none;"><td colspan="' + emptyColspan + '" class="cell-empty-message">No jurisdictions match the current filters.</td></tr>';
      tbody.innerHTML = html;
      if (dataType === 'digital_ids') {
        applyDigitalIdFilters();
      } else {
        applyStatusFilter();
      }
    }

    function escapeHtml(str) {
      if (str == null) return '';
      var s = String(str);
      return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    function updateTableEmptyState() {
      var tbody = document.getElementById('table-body');
      if (!tbody) return;
      var emptyRow = document.getElementById('table-empty-state');
      if (!emptyRow) return;
      var visibleCount = 0;
      (Array.from(tbody.children) as HTMLElement[]).forEach(function(row) {
        if (row.id === 'table-empty-state') return;
        if (row.style.display !== 'none') visibleCount++;
      });
      emptyRow.style.display = visibleCount === 0 ? '' : 'none';
    }

    function applyStatusFilter() {
      var tbody = document.getElementById('table-body');
      if (!tbody) return;
      var selectedStatuses = window.__SELECTED_STATUSES__;
      var level = (document.getElementById('jurisdiction-level-select') as HTMLInputElement | null)?.value || '';
      var lastParentShow = true;
      (Array.from(tbody.children) as HTMLElement[]).forEach(function(row) {
        if (row.id === 'table-empty-state') return;
        var status = row.getAttribute('data-status') || 'active';
        var isAccordion = row.classList.contains('table-row-accordion');
        var statusOk;
        if (isAccordion) {
          // For accordion parents, check whether any child region matches the selected statuses
          var childStatusAttr = row.getAttribute('data-child-statuses') || '';
          var childStatuses = childStatusAttr ? childStatusAttr.split(',') : [status];
          statusOk = !selectedStatuses || childStatuses.some(function(s) { return selectedStatuses.has(s); });
        } else {
          statusOk = !selectedStatuses || selectedStatuses.has(status);
        }
        // Check legislation tag filter
        var legTypeOk = true;
        var tagCountsAttr = row.getAttribute('data-leg-tag-counts');
        if (tagCountsAttr) {
          try {
            legTypeOk = matchesSelectedLegislationTags(JSON.parse(tagCountsAttr), window.__SELECTED_TAGS__);
          } catch(e) { legTypeOk = true; }
        }
        if (row.classList.contains('table-row-child')) {
          row.style.display = (lastParentShow && statusOk && legTypeOk) ? '' : 'none';
        } else if (row.classList.contains('eu-member-row')) {
          // EU member flat rows: only visible in national filter mode, hidden in all/eu/subnational
          var show = level === 'national' && statusOk && legTypeOk;
          row.style.display = show ? '' : 'none';
          // Don't let eu-member-row affect lastParentShow (they're not accordion parents)
        } else {
          var levelOk = !level || row.getAttribute('data-jurisdiction-level') === level;
          lastParentShow = levelOk && statusOk && legTypeOk;
          row.style.display = lastParentShow ? '' : 'none';
        }
      });
      updateTableEmptyState();
    }

    // Digital ID table filtering by jurisdiction level + Level of assurance +
    // Claims returned. The jurisdiction-level predicate must be composed here
    // (not only in applyStatusFilter) so a chip toggle never re-shows rows the
    // selected level has hidden. A row stays visible when its jurisdiction
    // level matches the selected level AND at least one of its levels of
    // assurance is still selected AND at least one of its claims is still
    // selected (rows with no value for a dimension are never hidden by that
    // dimension). The EUDI Wallets tab is the exception: while it is selected,
    // only rows with an EUDI wallet readiness stage stay visible.
    function applyDigitalIdFilters() {
      var tbody = document.getElementById('table-body');
      if (!tbody) return;
      var selLoa = window.__SELECTED_LOA__;
      var selClaims = window.__SELECTED_CLAIMS__;
      var eudiOnly = window.__EUDI_WALLET_FILTER__ === true;
      var level = (document.getElementById('jurisdiction-level-select') as HTMLInputElement | null)?.value || '';
      (Array.from(tbody.children) as HTMLElement[]).forEach(function(row) {
        if (row.id === 'table-empty-state') return;
        var loaAttr = row.getAttribute('data-loa');
        var claimsAttr = row.getAttribute('data-claims');
        var loaList = loaAttr ? loaAttr.split('|') : [];
        var claimsList = claimsAttr ? claimsAttr.split('|') : [];
        var levelOk = !level || row.getAttribute('data-jurisdiction-level') === level;
        var loaOk = !selLoa || loaList.length === 0 || loaList.some(function(v) { return selLoa.has(v); });
        var claimsOk = !selClaims || claimsList.length === 0 || claimsList.some(function(v) { return selClaims.has(v); });
        var eudiOk = !eudiOnly || (row.getAttribute('data-eudi') || '') !== '';
        row.style.display = (levelOk && loaOk && claimsOk && eudiOk) ? '' : 'none';
      });
      updateTableEmptyState();
    }

    var MS_X_SVG = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';

    // Reusable multi-select filter combobox. The Set on window[setKey] is the
    // source of truth (read by applyStatusFilter / applyDigitalIdFilters / the
    // globe), seeded with every value so "all selected" is the default. The
    // trigger shows the selection as removable tags, collapsing to a single
    // "All" tag; the panel offers type-ahead + a checkbox list with a
    // "Select all" row. Returns a reset() that re-selects everything.
    function initMultiSelectCombobox(container, onChange) {
      var setKey = container.getAttribute('data-ms-set');
      var trigger = container.querySelector('.ms-combobox-trigger') as HTMLElement;
      var panel = container.querySelector('[data-ms-panel]') as HTMLElement;
      var tagsEl = container.querySelector('[data-ms-tags]') as HTMLElement;
      var searchEl = container.querySelector('[data-ms-search]') as HTMLInputElement;
      var optionsEl = container.querySelector('[data-ms-options]') as HTMLElement;
      var noResultsEl = container.querySelector('[data-ms-no-results]') as HTMLElement;
      var allOption = container.querySelector('[data-ms-all]') as HTMLElement | null;
      var collapseAll = container.getAttribute('data-ms-collapse-all') !== 'false';
      var valueOptions = Array.from(container.querySelectorAll('.ms-combobox-option[data-ms-value]')) as HTMLElement[];
      var allValues = valueOptions.map(function(o) { return o.getAttribute('data-ms-value'); });

      function getSet() { return (window as any)[setKey] as Set<string>; }
      (window as any)[setKey] = new Set(allValues);

      function layoutTags() {
        var set = getSet();
        var pill = tagsEl.querySelector('.ms-combobox-overflow');
        if (pill) pill.parentNode.removeChild(pill);
        if (collapseAll && set.size === allValues.length) {
          tagsEl.innerHTML = '<span class="ms-combobox-tag ms-combobox-tag--all sticker sticker-grey"><span class="ms-combobox-tag-label">All</span></span>';
          return;
        }
        if (set.size === 0) {
          tagsEl.innerHTML = '<span class="ms-combobox-tag ms-combobox-tag--all sticker sticker-grey"><span class="ms-combobox-tag-label">None</span></span>';
          return;
        }
        tagsEl.innerHTML = valueOptions.filter(function(o) { return set.has(o.getAttribute('data-ms-value')); }).map(function(o) {
          var value = o.getAttribute('data-ms-value');
          var label = o.getAttribute('data-ms-label') || value;
          var sticker = o.getAttribute('data-ms-sticker') || 'sticker-grey';
          return '<span class="ms-combobox-tag sticker ' + sticker + '" data-ms-tag="' + escapeHtml(value) + '">' +
            '<span class="ms-combobox-tag-label">' + escapeHtml(label) + '</span>' +
            '<button type="button" class="ms-combobox-tag-remove" data-ms-remove="' + escapeHtml(value) + '" aria-label="Remove ' + escapeHtml(label) + '">' + MS_X_SVG + '</button>' +
          '</span>';
        }).join('');
        trimTagsToThreeRows();
      }

      // Keep at most three rows of tags, replacing the rest with a "+N" pill
      // whose title lists the hidden values. Rows are derived from distinct
      // flex-wrap offsets (not a fixed row height) so tags with wrapped,
      // multi-line text are measured correctly. Skipped while the trigger is
      // hidden (clientWidth 0); a ResizeObserver re-runs on reveal.
      function trimTagsToThreeRows() {
        var existingPill = tagsEl.querySelector('.ms-combobox-overflow');
        if (existingPill) existingPill.parentNode.removeChild(existingPill);
        var tags = Array.from(tagsEl.querySelectorAll('.ms-combobox-tag[data-ms-tag]')) as HTMLElement[];
        tags.forEach(function(t) { t.style.display = ''; });
        if (tagsEl.clientWidth === 0 || tags.length === 0) return;
        var rowTops = [];
        tags.forEach(function(t) {
          if (rowTops.indexOf(t.offsetTop) === -1) rowTops.push(t.offsetTop);
        });
        if (rowTops.length <= 3) return;
        var thirdRowTop = rowTops[2];
        var hidden = tags.filter(function(t) { return t.offsetTop > thirdRowTop; });
        if (hidden.length === 0) return;
        hidden.forEach(function(t) { t.style.display = 'none'; });
        var pill = document.createElement('span');
        pill.className = 'ms-combobox-tag ms-combobox-overflow sticker sticker-grey';
        tagsEl.appendChild(pill);
        function visibleTags() { return tags.filter(function(t) { return t.style.display !== 'none'; }); }
        pill.textContent = '+' + hidden.length;
        // The pill may itself land on a fourth row; hide visible tags from the
        // end until it fits, growing the count accordingly.
        while (pill.offsetTop > thirdRowTop && visibleTags().length > 0) {
          var vis = visibleTags();
          var last = vis[vis.length - 1];
          last.style.display = 'none';
          hidden.unshift(last);
          pill.textContent = '+' + hidden.length;
        }
        pill.title = hidden.map(function(t) {
          var lbl = t.querySelector('.ms-combobox-tag-label');
          return lbl ? lbl.textContent : '';
        }).join(', ');
      }

      function setOptionChecked(option, checked) {
        option.setAttribute('aria-selected', checked ? 'true' : 'false');
        var cb = option.querySelector('.ms-combobox-checkbox') as HTMLInputElement | null;
        if (cb) cb.checked = checked;
      }

      function syncOptionStates() {
        var set = getSet();
        valueOptions.forEach(function(o) {
          setOptionChecked(o, set.has(o.getAttribute('data-ms-value')));
        });
        if (allOption) setOptionChecked(allOption, set.size === allValues.length);
      }

      function refresh() {
        syncOptionStates();
        layoutTags();
      }

      function setValue(value, selected) {
        var set = getSet();
        if (selected) set.add(value); else set.delete(value);
        refresh();
        onChange();
      }

      function toggleValue(value) { setValue(value, !getSet().has(value)); }

      function toggleAll() {
        var set = getSet();
        if (set.size === allValues.length) {
          set.clear();
        } else {
          allValues.forEach(function(v) { set.add(v); });
        }
        refresh();
        onChange();
      }

      function selectAll() {
        var set = getSet();
        set.clear();
        allValues.forEach(function(v) { set.add(v); });
        refresh();
      }

      function applySearch() {
        var q = searchEl ? normalizeMs(searchEl.value) : '';
        var anyVisible = false;
        valueOptions.forEach(function(o) {
          var label = normalizeMs(o.getAttribute('data-ms-label') || '');
          var match = q === '' || label.indexOf(q) !== -1;
          o.style.display = match ? '' : 'none';
          if (match) anyVisible = true;
        });
        if (noResultsEl) noResultsEl.hidden = anyVisible;
      }

      function normalizeMs(value) {
        var text = String(value || '').toLowerCase();
        var n = typeof text.normalize === 'function' ? text.normalize('NFD') : text;
        return n.replace(/[\u0300-\u036f]/g, '');
      }

      function openPanel() {
        container.classList.add('open');
        trigger.setAttribute('aria-expanded', 'true');
        if (searchEl) searchEl.value = '';
        applySearch();
        clearHighlight();
        // Focus the search box when present; otherwise the options list so the
        // container keydown handler drives arrow/enter navigation.
        var focusTarget = searchEl || optionsEl;
        setTimeout(function() { focusTarget.focus(); }, 0);
      }

      function closePanel() {
        container.classList.remove('open');
        trigger.setAttribute('aria-expanded', 'false');
        clearHighlight();
      }

      var highlightedIndex = -1;
      function visibleOptionEls() {
        return Array.from(optionsEl.querySelectorAll('.ms-combobox-option')).filter(function(o) {
          return (o as HTMLElement).style.display !== 'none' && !(o as HTMLElement).hasAttribute('hidden');
        }) as HTMLElement[];
      }
      function clearHighlight() {
        highlightedIndex = -1;
        Array.from(optionsEl.querySelectorAll('.ms-combobox-option.highlighted')).forEach(function(o) { o.classList.remove('highlighted'); });
      }
      function highlight(index) {
        var opts = visibleOptionEls();
        opts.forEach(function(o, i) { o.classList.toggle('highlighted', i === index); });
        highlightedIndex = index;
        if (opts[index]) opts[index].scrollIntoView({ block: 'nearest' });
      }

      trigger.addEventListener('click', function() {
        if (container.classList.contains('open')) closePanel(); else openPanel();
      });

      if (searchEl) {
        searchEl.addEventListener('input', function() { applySearch(); clearHighlight(); });
      }

      // Keyboard nav lives on the container so it works whether focus is in the
      // search box or (when there is none) on the options list.
      container.addEventListener('keydown', function(e) {
        if (!container.classList.contains('open')) return;
        var opts = visibleOptionEls();
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          highlight(Math.min(highlightedIndex + 1, opts.length - 1));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          highlight(Math.max(highlightedIndex - 1, 0));
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (highlightedIndex >= 0 && opts[highlightedIndex]) opts[highlightedIndex].click();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          closePanel();
          trigger.focus();
        }
      });

      optionsEl.addEventListener('click', function(e) {
        var opt = (e.target as Element).closest('.ms-combobox-option') as HTMLElement | null;
        if (!opt) return;
        if (opt.hasAttribute('data-ms-all')) { toggleAll(); return; }
        var value = opt.getAttribute('data-ms-value');
        if (value != null) toggleValue(value);
      });

      tagsEl.addEventListener('click', function(e) {
        var btn = (e.target as Element).closest('[data-ms-remove]') as HTMLElement | null;
        if (!btn) return;
        e.stopPropagation();
        setValue(btn.getAttribute('data-ms-remove'), false);
      });

      document.addEventListener('click', function(e) {
        if (!(e.target as Element).closest('#' + container.id)) closePanel();
      });

      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(function() { trimTagsToThreeRows(); }).observe(tagsEl);
      }

      refresh();
      return { reset: selectAll };
    }

    var statusCombobox = null;
    var tagsCombobox = null;
    var loaCombobox = null;
    var claimsCombobox = null;

    (function() {
      var statusEl = document.getElementById('status-combobox');
      if (statusEl) {
        statusCombobox = initMultiSelectCombobox(statusEl, function() {
          applyStatusFilter();
          if (window.globe) window.globe.applyModeColors();
        });
      }
      var tagsEl = document.getElementById('legislation-tag-combobox');
      if (tagsEl) {
        tagsCombobox = initMultiSelectCombobox(tagsEl, function() {
          applyStatusFilter();
          if (window.globe) window.globe.applyModeColors();
        });
      }
      var loaEl = document.getElementById('loa-combobox');
      if (loaEl) loaCombobox = initMultiSelectCombobox(loaEl, function() {
        applyDigitalIdFilters();
        if (window.globe) window.globe.applyModeColors();
      });
      var claimsEl = document.getElementById('claims-combobox');
      if (claimsEl) claimsCombobox = initMultiSelectCombobox(claimsEl, function() {
        applyDigitalIdFilters();
        if (window.globe) window.globe.applyModeColors();
      });
    })();

    function resetStatusFilter() {
      if (statusCombobox) statusCombobox.reset();
      if (tagsCombobox) tagsCombobox.reset();
      applyStatusFilter();
    }

    function resetDigitalIdFilters() {
      if (loaCombobox) loaCombobox.reset();
      if (claimsCombobox) claimsCombobox.reset();
    }

    // Table row click – accordion expand/collapse for countries with regions, or open drawer for single rows/state rows
    var tableBody = document.getElementById('table-body');
    if (tableBody) {
      tableBody.addEventListener('click', function(e) {
        var row = (e.target as Element).closest('tr.table-row');
        if (!row) return;
        if (row.classList.contains('table-row-accordion')) {
          var countryCode = row.getAttribute('data-country-code');
          // Open drawer for the country (accordion is always expanded, no toggle)
          if (countryCode && window.openDrawer) {
            window.openDrawer('/region/' + countryCode.toLowerCase(), countryCode);
          }
          return;
        }
        var href = row.getAttribute('data-href');
        if (href && window.openDrawer) {
          var code = href.split('/').pop().toUpperCase();
          window.openDrawer(href, code);
        }
      });
    }

    // Table column sorting (default: jurisdiction ascending). Uses event
    // delegation on <thead> + dynamic column lookup so it keeps working after
    // the header is rewritten for a different data type (different columns).
    (function() {
      var tbody = document.getElementById('table-body');
      var thead = tbody && tbody.closest('table') && tbody.closest('table').querySelector('thead');
      if (!tbody || !thead) return;

      function getHeaderCells() {
        return Array.from(thead.querySelectorAll('th')) as HTMLElement[];
      }

      function getColIndex(col) {
        var cells = getHeaderCells();
        for (var i = 0; i < cells.length; i++) {
          if (cells[i].getAttribute('data-sort') === col) return i;
        }
        return -1;
      }

      function getRowBlocks() {
        var blocks = [];
        var rows = Array.from(tbody.children);
        var i = 0;
        while (i < rows.length) {
          var row = rows[i];
          if (row.id === 'table-empty-state' || row.classList.contains('table-row-child')) { i++; continue; }
          var block = [row];
          var parentCode = row.getAttribute('data-country-code');
          i++;
          while (i < rows.length) {
            var next = rows[i];
            if (next.classList.contains('table-row-child') && next.getAttribute('data-accordion-parent') === parentCode) {
              block.push(next);
              i++;
            } else break;
          }
          blocks.push(block);
        }
        return blocks;
      }

      function getCellText(tr, colIndex) {
        var cell = tr.cells[colIndex];
        return cell ? cell.textContent.trim() : '';
      }

      function applySort() {
        var blocks = getRowBlocks();
        var colIndex = getColIndex(sortColumn);
        if (colIndex < 0) return;
        blocks.sort(function(a, b) {
          var va = getCellText(a[0], colIndex);
          var vb = getCellText(b[0], colIndex);
          var c = va.localeCompare(vb, undefined, { sensitivity: 'base' });
          return sortDir === 'asc' ? c : -c;
        });
        blocks.forEach(function(block) {
          block.forEach(function(row) { tbody.appendChild(row); });
        });
      }

      function updateHeaderState() {
        getHeaderCells().forEach(function(th) {
          var col = th.getAttribute('data-sort');
          th.classList.remove('sort-asc', 'sort-desc');
          th.setAttribute('aria-sort', 'none');
          if (col === sortColumn) {
            th.classList.add(sortDir === 'asc' ? 'sort-asc' : 'sort-desc');
            th.setAttribute('aria-sort', sortDir === 'asc' ? 'ascending' : 'descending');
          }
        });
      }

      thead.addEventListener('click', function(e) {
        var th = (e.target as Element).closest('th.sortable') as HTMLElement | null;
        if (!th) return;
        var col = th.getAttribute('data-sort');
        if (col === sortColumn) {
          sortDir = sortDir === 'asc' ? 'desc' : 'asc';
        } else {
          sortColumn = col;
          sortDir = 'asc';
        }
        updateHeaderState();
        applySort();
      });
    })();

    // Combobox typeahead functionality
    (function() {
      var searchInput = document.getElementById('search-input') as HTMLInputElement;
      var dropdown = document.getElementById('combobox-dropdown');
      var highlightedIndex = -1;
      var filteredResults = [];

      function renderDropdown(results) {
        filteredResults = results;
        highlightedIndex = -1;
        
        if (results.length === 0) {
          dropdown.innerHTML = '<div class="combobox-no-results">No jurisdictions found</div>';
        } else {
          dropdown.innerHTML = results.map(function(item, index) {
            return '<div class="combobox-option" data-index="' + index + '" data-href="' + item.href + '" data-code="' + item.code + '">' +
              item.name + (item.count > 1 ? '<span class="combobox-option-count">(' + item.count + ')</span>' : '') + '</div>';
          }).join('');
        }
        dropdown.classList.add('open');
      }

      function closeDropdown() {
        dropdown.classList.remove('open');
        highlightedIndex = -1;
      }

      function highlightOption(index) {
        var options = dropdown.querySelectorAll('.combobox-option');
        options.forEach(function(opt, i) {
          opt.classList.toggle('highlighted', i === index);
        });
        highlightedIndex = index;
        
        // Scroll into view if needed
        if (options[index]) {
          options[index].scrollIntoView({ block: 'nearest' });
        }
      }

      function normalizeSearchText(value) {
        var text = String(value || '').toLowerCase();
        var normalized = typeof text.normalize === 'function' ? text.normalize('NFD') : text;
        return normalized.replace(/[\u0300-\u036f]/g, '');
      }

      function getComboboxMatches(data, search) {
        var normalizedSearch = normalizeSearchText(search);
        if (normalizedSearch === '') return data;
        return data.filter(function(item) {
          return (item.searchName || normalizeSearchText(item.name)).includes(normalizedSearch);
        });
      }

      function selectOption(href, countryCode) {
        if (window.openDrawer) {
          window.openDrawer(href, countryCode);
        }
        searchInput.value = '';
        closeDropdown();
        collapseFilterContentIfExpanded();
      }

      searchInput.addEventListener('input', function(e) {
        var search = (e.target as HTMLInputElement).value.trim();
        var data = getComboboxData();
        renderDropdown(getComboboxMatches(data, search));
      });

      searchInput.addEventListener('focus', function() {
        var search = searchInput.value.trim();
        var data = getComboboxData();
        renderDropdown(getComboboxMatches(data, search));
      });

      searchInput.addEventListener('keydown', function(e) {
        var options = dropdown.querySelectorAll('.combobox-option');
        
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (highlightedIndex < options.length - 1) {
            highlightOption(highlightedIndex + 1);
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (highlightedIndex > 0) {
            highlightOption(highlightedIndex - 1);
          }
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (highlightedIndex >= 0 && filteredResults[highlightedIndex]) {
            selectOption(filteredResults[highlightedIndex].href, filteredResults[highlightedIndex].code);
          }
        } else if (e.key === 'Escape') {
          closeDropdown();
          searchInput.blur();
        }
      });

      dropdown.addEventListener('click', function(e) {
        var option = (e.target as Element).closest('.combobox-option');
        if (option) {
          var href = option.getAttribute('data-href');
          var code = option.getAttribute('data-code');
          selectOption(href, code);
        }
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', function(e) {
        if (!(e.target as Element).closest('#jurisdiction-combobox')) {
          closeDropdown();
        }
      });
    })();

    // Jurisdiction level filter
    var jurisdictionLevelSelect = document.getElementById('jurisdiction-level-select') as HTMLInputElement | null;
    if (jurisdictionLevelSelect) {
      // Sync dropdown from saved regulation level on load (globe reads from localStorage)
      (function syncJurisdictionSelectFromStorage() {
        var stored = localStorage.getItem('regulationLevel');
        var value = stored === 'national' ? 'national' : stored === 'regional' ? 'subnational' : stored === 'eu' ? 'eu' : '';
        jurisdictionLevelSelect.value = value;
      })();
      jurisdictionLevelSelect.addEventListener('change', function() {
        var selectValue = jurisdictionLevelSelect.value;
        var regulationLevel = selectValue === 'national' ? 'national' : selectValue === 'subnational' ? 'regional' : selectValue === 'eu' ? 'eu' : 'platform';
        if (window.globe) window.globe.setRegulationLevel(regulationLevel);
        if (typeof applyStatusFilter === 'function') applyStatusFilter();
        var searchInputEl = document.getElementById('search-input');
        if (searchInputEl && document.getElementById('combobox-dropdown').classList.contains('open')) {
          searchInputEl.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
    }

    // Apply status + jurisdiction + tags filters to the initial server-rendered table.
    // Comboboxes seed window.__SELECTED_STATUSES__ / window.__SELECTED_TAGS__ on init above.
    if (typeof applyStatusFilter === 'function') applyStatusFilter();

    return {
      renderTableBody: renderTableBody,
      applyStatusFilter: applyStatusFilter,
      applyDigitalIdFilters: applyDigitalIdFilters,
      resetDigitalIdFilters: resetDigitalIdFilters,
      resetStatusFilter: resetStatusFilter,
    };
}
